import { useEffect, useState, useCallback, useRef } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  Timestamp,
  QuerySnapshot,
  DocumentData,
} from 'firebase/firestore';
import { db } from '@clareefai/api/firebase';
import {
  playNotificationSound,
  setDocumentTitleWithCount,
  showBrowserNotification,
} from '@clareefai/utils';

export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  createdAt: number;
  updatedAt: number;
  [key: string]: unknown;
}

export interface UseNotificationsOptions {
  /** Firestore collection path: "vendors" | "promoters" | "prospects" | "admins" */
  collectionPath: string;
  /** Firebase UID of the current user (null when logged out) */
  firebaseUid: string | null;
  /** App name for document title, e.g. "Clareefai Vendor" */
  appName: string;
  /**
   * Fires when notifications strictly newer than the last seen timestamp arrive.
   * The first snapshot is treated as baseline (historical) and never fires.
   * Read-status updates do not fire — only real new notifications do.
   */
  onNewNotifications?: (newNotifications: Notification[]) => void;
  /**
   * When false, the hook is inert: no Firestore subscription, no sound,
   * no browser notification, no tab-title updates. Useful when another
   * component already owns a `useNotifications` subscription and this
   * instance would just duplicate it.
   * Default: true.
   */
  enabled?: boolean;
}

export interface UseNotificationsResult {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

export function useNotifications({
  collectionPath,
  firebaseUid,
  appName,
  onNewNotifications,
  enabled = true,
}: UseNotificationsOptions): UseNotificationsResult {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const prevUnreadRef = useRef(-1);
  const lastSeenCreatedAtRef = useRef<number | null>(null);
  const onNewNotificationsRef = useRef(onNewNotifications);
  useEffect(() => {
    onNewNotificationsRef.current = onNewNotifications;
  }, [onNewNotifications]);

  useEffect(() => {
    if (!enabled) return;
    setDocumentTitleWithCount(appName, unreadCount);
  }, [appName, unreadCount, enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (!firebaseUid) {
      setLoading(false);
      return;
    }

    const notificationsRef = collection(db, collectionPath, firebaseUid, 'notifications');
    const q = query(
      notificationsRef,
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const notifs: Notification[] = [];
        let unread = 0;

        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const notification: Notification = {
            id: docSnap.id,
            type: data.type || '',
            title: data.title || '',
            message: data.message || '',
            read: data.read || false,
            createdAt: data.createdAt || 0,
            updatedAt: data.updatedAt || 0,
            ...data,
          };
          notifs.push(notification);
          if (!notification.read) unread++;
        });

        const isNewNotification = prevUnreadRef.current >= 0 && unread > prevUnreadRef.current;
        prevUnreadRef.current = unread;
        if (isNewNotification) {
          playNotificationSound();
          const newest = notifs.find((n) => !n.read);
          if (newest) showBrowserNotification(newest.title, { body: newest.message });
        }

        const baseline = lastSeenCreatedAtRef.current;
        const maxCreatedAt = notifs.reduce((m, n) => (n.createdAt > m ? n.createdAt : m), 0);
        if (baseline === null) {
          lastSeenCreatedAtRef.current = maxCreatedAt;
        } else if (maxCreatedAt > baseline) {
          const fresh = notifs.filter((n) => n.createdAt > baseline);
          lastSeenCreatedAtRef.current = maxCreatedAt;
          if (fresh.length > 0) onNewNotificationsRef.current?.(fresh);
        }

        setNotifications(notifs);
        setUnreadCount(unread);
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to notifications:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [collectionPath, firebaseUid, enabled]);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!firebaseUid) return;
      try {
        const notificationRef = doc(db, collectionPath, firebaseUid, 'notifications', notificationId);
        await updateDoc(notificationRef, {
          read: true,
          updatedAt: Timestamp.now().toMillis(),
        });
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    },
    [collectionPath, firebaseUid]
  );

  const markAllAsRead = useCallback(
    async () => {
      if (!firebaseUid) return;
      try {
        const unreadNotifications = notifications.filter((n) => !n.read);
        await Promise.all(
          unreadNotifications.map((notification) => {
            const notificationRef = doc(db, collectionPath, firebaseUid, 'notifications', notification.id);
            return updateDoc(notificationRef, {
              read: true,
              updatedAt: Timestamp.now().toMillis(),
            });
          })
        );
      } catch (error) {
        console.error('Error marking all notifications as read:', error);
      }
    },
    [collectionPath, firebaseUid, notifications]
  );

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
  };
}
